/**
 * The front faces of blocks (05 §5.1): each level's tiles are cut in one style. The creases
 * between the faces are what the post pass turns into 1 px highlights, so every style is
 * made of bevels and steps, not painted lines. Sizes are in tiles; at 14 texels a tile,
 * 0.14 is a 2-texel bevel.
 */

import { type Color, Vector3 } from 'three';
import type { MeshBuilder } from '../kit/MeshBuilder';
import type { Palette } from '../palette';

export type BlockStyle = 'pillow' | 'brick' | 'slab' | 'stud' | 'pyramid' | 'groove';
export const BLOCK_STYLES: readonly BlockStyle[] = ['pillow', 'brick', 'slab', 'stud', 'pyramid', 'groove'];

const BEVEL = 0.14;
const BEVEL_DEPTH = 0.1;

const v = (x: number, y: number, z: number): Vector3 => new Vector3(x, y, z);

/** A face inset on all four sides and pushed forward from `z0` to `z1`: a raised pillow. */
function pillow(
  mb: MeshBuilder, x: number, y: number, w: number, h: number, z0: number, z1: number, face: Color, bevel: Color,
  inset = BEVEL,
): void {
  const i = inset;
  const o = [v(x, y, z0), v(x + w, y, z0), v(x + w, y + h, z0), v(x, y + h, z0)] as const;
  const n = [v(x + i, y + i, z1), v(x + w - i, y + i, z1), v(x + w - i, y + h - i, z1), v(x + i, y + h - i, z1)] as const;
  mb.quad(n[0], n[1], n[2], n[3], face);
  mb.quad(o[0], o[1], n[1], n[0], bevel); // bottom
  mb.quad(o[1], o[2], n[2], n[1], bevel); // right
  mb.quad(o[2], o[3], n[3], n[2], face); // top: lit like the face, so the light comes from above
  mb.quad(o[3], o[0], n[0], n[3], bevel); // left
}

function flat(mb: MeshBuilder, x: number, y: number, w: number, h: number, z: number, c: Color): void {
  mb.quad(v(x, y, z), v(x + w, y, z), v(x + w, y + h, z), v(x, y + h, z), c);
}

/** One tile's front face in `style`. `front` is the z of the block's front plane. */
export function front(
  mb: MeshBuilder, style: BlockStyle, col: number, row: number, x: number, y: number, front: number, p: Palette,
): void {
  const face = p.color('block.2');
  const shade = p.color('block.1');
  const back = front - BEVEL_DEPTH;
  switch (style) {
    case 'pillow':
      pillow(mb, x, y, 1, 1, back, front, face, shade);
      return;
    case 'brick': {
      // Two-tile bricks, offset every other row; mortar is the blockAlt ramp.
      const mortar = p.color('blockAlt.0');
      const top = 0.14;
      flat(mb, x, y + 1 - top, 1, top, back, mortar);
      if ((col + (row % 2)) % 2 === 0) {
        flat(mb, x, y, 0.12, 1 - top, back, mortar);
        pillow(mb, x + 0.12, y, 0.88, 1 - top, back, front, face, shade);
      } else {
        pillow(mb, x, y, 1, 1 - top, back, front, face, shade);
      }
      return;
    }
    case 'slab':
      // Quiet, nearly flat tiles: a one-texel bevel, so a dense level still shows its grid.
      pillow(mb, x, y, 1, 1, back + 0.05, front, face, shade, 0.07);
      return;
    case 'stud':
      // A flat plate with a raised square boss in the middle of every tile.
      flat(mb, x, y, 1, 1, back, shade);
      pillow(mb, x + 0.22, y + 0.22, 0.56, 0.56, back, front, face, p.color('block.3'), 0.1);
      return;
    case 'pyramid': {
      // Four facets rising to a point: the classic diamond-cut tile.
      const c = v(x + 0.5, y + 0.5, front);
      const a = [v(x, y, back), v(x + 1, y, back), v(x + 1, y + 1, back), v(x, y + 1, back)] as const;
      mb.tri(a[0], a[1], c, shade);
      mb.tri(a[1], a[2], c, face);
      mb.tri(a[2], a[3], c, p.color('block.3'));
      mb.tri(a[3], a[0], c, face);
      return;
    }
    case 'groove':
      // Two raised bars with a sunken groove between them.
      pillow(mb, x, y + 0.55, 1, 0.45, back, front, face, shade, 0.08);
      flat(mb, x, y + 0.42, 1, 0.13, back - 0.06, p.color('blockAlt.1'));
      pillow(mb, x, y, 1, 0.42, back, front, face, shade, 0.08);
      return;
  }
}
